import React, { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { Check16, Check24 } from '@honeybook/icons';
import { TSize } from '../../tokens/types';
import HBDivider from '../HBDivider/HBDivider';
import HBText from '../HBText/HBText';
import { vars } from '../../theme';
import type { TMenuItem, IMenuItem } from './HBMenuItemOld.types';

export interface Props {
    item: TMenuItem;
    closeMenu: () => void;
    className?: string;
    size?: Extract<TSize, 'md' | 'lg'>;
}

const HBMenuItemOld = ({ item, closeMenu, className, size = 'md' }: Props) => {
    const handleClick: MouseEventHandler<HTMLDivElement> = ev => {
        if ((item as IMenuItem).disabled) {
            return;
        }
        closeMenu();

        if ('onClick' in item) {
            item.onClick(ev);
        }
    };

    const ConditionalWrapper = ({ children }) => {
        return (item as IMenuItem).itemWrapper
            ? (item as IMenuItem).itemWrapper({ children })
            : children;
    };

    if ('title' in item) {
        return (
            <ConditionalWrapper>
                <Container
                    size={size}
                    className={className}
                    style={item.style}
                    onClick={handleClick}
                    rightIcon={item.setTextBeforeIcon}
                    disabled={item.disabled}
                    isSelected={item.isSelected}
                >
                    {!item.setTextBeforeIcon && item.icon && (
                        <LeftIcon>{item.icon}</LeftIcon>
                    )}
                    {item.title}
                    {item.setTextBeforeIcon && item.icon && (
                        <RightIcon>{item.icon}</RightIcon>
                    )}
                    {item.isSelected &&
                        (() => {
                            switch (size) {
                                default:
                                case 'md':
                                    return (
                                        <Check16Icon
                                            color={vars.colors.text.primary}
                                        />
                                    );
                                case 'lg':
                                    return (
                                        <Check24Icon
                                            color={vars.colors.text.primary}
                                        />
                                    );
                            }
                        })()}
                </Container>
            </ConditionalWrapper>
        );
    }

    if ('sectionTitle' in item) {
        return (
            <Section>
                <HBText type="body14" color="text.tertiary">
                    {item.sectionTitle}
                </HBText>
            </Section>
        );
    }

    if ('isDivider' in item) {
        return <HBDivider margin={item.margin} />;
    }
    return null;
};

export default HBMenuItemOld;

// ------------- Styles ---------------//

const LeftIcon = styled.div`
    height: 16px;
    padding-right: 12px;
`;

const RightIcon = styled.div`
    height: 16px;
`;
const Check16Icon = styled(Check16)`
    height: 16px;
    margin-left: auto;
`;
const Check24Icon = styled(Check24)`
    height: 24px;
    margin-left: auto;
`;

const Container = styled.div<{
    rightIcon: boolean;
    disabled: boolean;
    isSelected?: boolean;
    size?: TSize;
}>`
    display: flex;
    align-items: center;
    height: ${({ size }) => {
        switch (size) {
            default:
            case 'md':
                return 42;
            case 'lg':
                return 56;
        }
    }}px;
    ${({ size }) =>
        size === 'lg'
            ? css`
                  font-size: 18px;
              `
            : ''}
    padding: 0 20px;
    ${props => (props.rightIcon ? 'justify-content: space-between;' : null)}
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

    ${({ isSelected }) =>
        isSelected &&
        css`
            background-color: ${vars.colors.surface.itemSelected};
        `}

    &:hover {
        background-color: ${props =>
            props.disabled ? null : vars.colors.surface.itemHover};
    }
`;

const Section = styled.div`
    height: 42px;
    padding: 0 15px;
    display: flex;
    align-items: flex-end;
    cursor: default;
`;
